// @wc-ignore-file
/**
 * CR-202 S4: the voice dictation language picked in the composer, remembered
 * per user in this browser. With no stored choice it follows the Kairo UI
 * language. A per-viewer convenience: storage failures only forget the choice.
 */
import { LOCALE_KEY } from "@/lib/local-storage.consts";
import { useCallback, useEffect, useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import {
  type DictationLanguage,
  defaultDictationLanguage,
  readStoredDictationLanguage,
  recognitionLangFor,
  storeDictationLanguage,
} from "../voice-dictation";

function browserStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

export interface DictationLanguageState {
  choice: DictationLanguage;
  setChoice: (next: DictationLanguage) => void;
  /** BCP-47 tag of an explicit choice; undefined for "browser" (the hook uses navigator.language). */
  lang: string | undefined;
}

export function useDictationLanguage(userId: string): DictationLanguageState {
  const [appLocale] = useLocalStorage<string>(LOCALE_KEY, "en");
  const [stored, setStored] = useState<DictationLanguage | null>(() =>
    readStoredDictationLanguage(browserStorage(), userId),
  );

  // Another user signing in on the same page reads their own choice (the
  // mount run only repeats the initializer's read).
  useEffect(() => {
    setStored(readStoredDictationLanguage(browserStorage(), userId));
  }, [userId]);

  const setChoice = useCallback(
    (next: DictationLanguage) => {
      setStored(next);
      storeDictationLanguage(browserStorage(), userId, next);
    },
    [userId],
  );

  const choice = stored ?? defaultDictationLanguage(appLocale);
  return {
    choice,
    setChoice,
    lang: recognitionLangFor(choice),
  };
}
